import React, { useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import Header1 from '../common/Header1';
import Footer1 from '../common/Footer1';
import ScrollManager from '../layout/scrollManager';
import SideContactBar from '../layout/SideContactBar';
import SEOHead from '../layout/SEOHead';
import { SITE_URL } from '../../config/seo';

const faqs = [
    {
        category: "General",
        questions: [
            {
                q: "What services does Urban Shine Laundry offer?",
                a: "We offer Dry Cleaning, Steam Ironing, Wash & Iron, Wash & Fold, Carpet Cleaning, Curtain Cleaning, Shoe Cleaning, and Leather Cleaning. All services include free home pickup and delivery."
            },
            {
                q: "Do you offer home pickup and delivery?",
                a: "Yes! Free home pickup and delivery is available for orders above ₹250. Our team will collect your items from your doorstep and return them clean and neatly packed."
            },
            {
                q: "What are your operating hours?",
                a: "We are open from 9:00 AM to 7:30 PM, Thursday to Tuesday (closed on Wednesdays)."
            },
            {
                q: "Where are you located?",
                a: "Our store is at DF 05, Street Number 316, DF Block, New Town, Kolkata 700156. You can also find us on Google Maps."
            },
        ]
    },
    {
        category: "Pricing & Payment",
        questions: [
            {
                q: "Are prices inclusive of taxes?",
                a: "Yes, all our listed prices are inclusive of applicable taxes. There are no hidden charges."
            },
            {
                q: "Is there a minimum order value?",
                a: "Yes, the minimum order value for free pickup and delivery is ₹250. For orders below this, a nominal delivery fee may apply."
            },
            {
                q: "Can dry cleaning prices vary from the listed price?",
                a: "Yes, actual dry cleaning prices may vary depending on the garment's fabric, texture, embellishments, level of embroidery, or similar work. We will always inform you of any price difference before proceeding."
            },
            {
                q: "Do you offer any discounts?",
                a: "Yes! We offer 20% off on your first order. We also run seasonal promotions — follow us on WhatsApp or social media to stay updated."
            },
            {
                q: "What payment methods do you accept?",
                a: "We accept cash, UPI (GPay, PhonePe, Paytm), and other digital payment methods. Payment is collected at the time of delivery."
            },
        ]
    },
    {
        category: "Orders & Pickup",
        questions: [
            {
                q: "How do I book a pickup?",
                a: "You can book a pickup directly through our website by clicking 'Book Now' or 'Schedule Free Pickup', via WhatsApp at +91 84509 03882, or by calling us directly."
            },
            {
                q: "What is the typical turnaround time?",
                a: "Standard turnaround is 24–48 hours for most services. Dry cleaning and specialty services may take 48–72 hours. Same-day service is available for select services — please confirm availability when booking."
            },
            {
                q: "What is the minimum quantity for KG-based services?",
                a: "A minimum of 5 kg of garments is required to qualify for our KG-based services such as Wash & Fold."
            },
            {
                q: "Can I track my order?",
                a: "Yes, you can track your order status through our app. You will also receive updates via WhatsApp or call from our team."
            },
            {
                q: "What if I need to reschedule my pickup?",
                a: "Please contact us at least 2 hours before the scheduled pickup time to reschedule. You can reach us via WhatsApp at +91 84509 03882 or by calling directly."
            },
        ]
    },
    {
        category: "Garment Care & Quality",
        questions: [
            {
                q: "How do you handle delicate or expensive garments?",
                a: "Each garment is individually inspected before processing. Delicate fabrics receive special handling and are cleaned using methods appropriate for their material. We always follow care label instructions."
            },
            {
                q: "What happens if my garment is damaged?",
                a: "While we take every precaution, if a garment is damaged due to our negligence, we will assess the situation and offer appropriate compensation or resolution. Please report any concerns within 24 hours of receiving your items."
            },
            {
                q: "Do you use eco-friendly cleaning products?",
                a: "Yes, we use environmentally responsible cleaning solvents and detergents wherever possible. We are committed to reducing our environmental footprint without compromising on cleaning quality."
            },
            {
                q: "Can you remove all types of stains?",
                a: "We apply targeted pre-treatment to stains before cleaning. While we can remove the vast majority of stains, some stains (such as old, set-in stains or certain chemical stains) may not be fully removable. We will always advise you of this beforehand."
            },
            {
                q: "Do you reserve the right to refuse service?",
                a: "Yes, we reserve the right to refuse service for heavily soiled, infested, or severely damaged garments that may pose a risk to our equipment or staff."
            },
        ]
    },
    {
        category: "Contact & Support",
        questions: [
            {
                q: "How can I contact Urban Shine Laundry?",
                a: "You can reach us via email at urbanshinelaundry46@gmail.com, call/WhatsApp us at +91 84509 03882, or visit our store at DF 05, Street Number 316, DF Block, New Town, Kolkata 700156."
            },
            {
                q: "What if I have a complaint?",
                a: "We take all feedback seriously. Please contact us within 24 hours of receiving your items. You can reach us via WhatsApp, email, or phone and we will resolve your concern promptly."
            },
        ]
    },
];

const FAQItem = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
            <button
                className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-green-50 transition-colors"
                onClick={() => setOpen(!open)}
            >
                <span className="font-medium text-gray-800 pr-4">{q}</span>
                {open
                    ? <ChevronUp size={20} className="text-green-600 flex-shrink-0" />
                    : <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                }
            </button>
            {open && (
                <div className="px-6 py-4 bg-gray-50 text-gray-700 border-t border-gray-200">
                    {a}
                </div>
            )}
        </div>
    );
};

const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.flatMap(section =>
        section.questions.map(item => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
        }))
    ),
};

const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${SITE_URL}/faq` },
    ],
};

const FAQ = () => {
    return (
        <ScrollManager>
            <SEOHead
                title="FAQ – Urban Shine Laundry | Laundry & Dry Cleaning Questions Answered"
                description="Find answers to common questions about Urban Shine Laundry's services, pricing, pickup & delivery, garment care, and more. Expert laundry service in New Town, Kolkata."
                keywords="Urban Shine Laundry FAQ, laundry questions Kolkata, dry cleaning FAQ, pickup delivery questions, garment care FAQ, laundry pricing New Town"
                canonicalPath="/faq"
                ogType="article"
                structuredData={[faqSchema, breadcrumbSchema]}
                publishedTime="2025-06-01T00:00:00+05:30"
            />
            <Header1 />
            <SideContactBar />
            <br /><br /><br />
            <section className="py-16 md:py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium mb-4">Help Centre</span>
                        <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            Find answers to common questions about our laundry services, pricing, pickup &amp; delivery, and more.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto mb-16">
                        {faqs.map((section) => (
                            <div key={section.category} className="mb-10">
                                <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center">
                                    <span className="w-2 h-6 bg-green-600 rounded mr-3 inline-block"></span>
                                    {section.category}
                                </h2>
                                {section.questions.map((item, idx) => (
                                    <FAQItem key={idx} q={item.q} a={item.a} />
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Still have questions */}
                    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 text-center mb-16">
                        <div className="flex justify-center mb-4">
                            <div className="p-4 bg-green-100 rounded-full">
                                <MessageCircle size={32} className="text-green-600" />
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">Still have questions?</h3>
                        <p className="text-gray-600 mb-6">
                            Can't find what you're looking for? Our team is happy to help.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="https://wa.me/918450903882"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors"
                            >
                                WhatsApp Us
                            </a>
                            <a
                                href="mailto:urbanshinelaundry46@gmail.com"
                                className="inline-flex items-center justify-center px-6 py-3 border-2 border-green-600 text-green-600 rounded-full font-medium hover:bg-green-600 hover:text-white transition-colors"
                            >
                                Email Us
                            </a>
                        </div>
                    </div>
                </div>
            </section>
            <Footer1 />
        </ScrollManager>
    );
};

export default FAQ;
